import React, { useEffect, useState } from 'react';
import { HiOutlineDocumentDuplicate } from 'react-icons/hi2';
import { toast } from 'react-toastify';

interface ClientReservation {
  reservation_id: number;
  client_id: number | null;
  client_name: string;
  email: string;
  phone_number: string;
  reservation_date: string;
  local_id: number;
  init: number;
  end: number;
  n_people: number;
  mesa_id: number;
  mesa_num: number;
  tag?: string;
  description: string;
  confirmed: boolean;
  approved: boolean;
  attended: boolean;
  cancelled: boolean;
  arrived: boolean;
}

interface TableProps {
  reservations: ClientReservation[];
  setReservations: (reservations: ClientReservation[]) => void;
  startDate?: string;
  setStartDate?: (date: string) => void;
  endDate?: string;
  setEndDate?: (date: string) => void;
}

function slotToTime(slot: number) {
  if (slot === undefined || slot === null || isNaN(slot)) return 'N/A';
  const hours = Math.floor(slot / 4);
  const minutes = (slot % 4) * 15;
  const ampm = hours < 12 ? 'AM' : 'PM';
  return `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

const formatReservationDate = (date: string): string => {
  const [year, month, day] = date.split('-');
  return `${day}-${month}-${year}`;
};

const STATUS_CONFIG: Record<string, { label: string; badge: string }> = {
  cancelled:   { label: 'Cancelada',    badge: 'mp-badge mp-badge-danger' },
  attended:    { label: 'Atendida',     badge: 'mp-badge mp-badge-success' },
  confirmed:   { label: 'Confirmada',   badge: 'mp-badge mp-badge-success' },
  pending:     { label: 'Pendiente',    badge: 'mp-badge mp-badge-warning' },
  unapproved:  { label: 'No Aprobada',  badge: 'mp-badge mp-badge-neutral' },
};

const Table: React.FC<TableProps> = ({ reservations, setReservations, startDate, setStartDate, endDate, setEndDate }) => {
  const [currentTableData, setCurrentTableData] = useState<ClientReservation[]>([]);

  const now = new Date();
  const today = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0')
  ].join('-');

  const [appliedStartDate, setAppliedStartDate] = useState<string | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<string | null>(null);

  useEffect(() => {
    setAppliedStartDate(startDate ?? null);
    setAppliedEndDate(endDate ?? null);
  }, [startDate, endDate, today]);

  useEffect(() => {
    let filtered = [...reservations];
    if (appliedStartDate && appliedEndDate) {
      filtered = filtered.filter(res =>
        res.reservation_date >= appliedStartDate && res.reservation_date <= appliedEndDate
      );
    }
    filtered.sort((a, b) => {
      const priority = (r: ClientReservation) => {
        if (r.cancelled) return 5;
        if (!r.approved && !r.cancelled) return 4;
        if (r.attended && r.approved && r.confirmed) return 3;
        if (r.approved && r.confirmed) return 2;
        if (!r.confirmed && r.approved) return 1;
        return 6;
      };
      return priority(a) - priority(b);
    });
    setCurrentTableData(filtered);
  }, [reservations, appliedStartDate, appliedEndDate, today]);

  const getStatus = (res: ClientReservation): string => {
    if (res.cancelled) return 'cancelled';
    if (res.attended && res.approved && res.confirmed) return 'attended';
    if (res.confirmed && !res.attended) return 'confirmed';
    if (res.approved && !res.confirmed && !res.cancelled) return 'pending';
    if (!res.approved && !res.confirmed && !res.cancelled) return 'unapproved';
    return 'pending';
  };

  const handleCopyPhone = (phone: string) => {
    navigator.clipboard.writeText(phone)
      .then(() => toast.success('N\u00famero copiado'))
      .catch(() => toast.error('Error al copiar'));
  };

  return (
    <div className="mp-table-card">
      <div className="overflow-x-auto overflow-y-auto mp-scrollbar" style={{ maxHeight: '70vh' }}>
        <table className="mp-table">
          <thead className="mp-table-head">
            <tr>
              <th>Cliente</th>
              <th>Tel\u00e9fono</th>
              <th className="hidden md:table-cell">Fecha</th>
              <th>Hora</th>
              <th>Personas</th>
              <th className="hidden lg:table-cell">Mesa</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody className="mp-table-body">
            {currentTableData.map((res) => {
              const status = getStatus(res);
              const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
              return (
                <tr key={res.reservation_id}>
                  <td className="font-medium">{res.client_name}</td>
                  <td>
                    <div className="flex items-center justify-center gap-1.5">
                      <span>{res.phone_number}</span>
                      <button
                        className="mp-btn-icon"
                        onClick={() => handleCopyPhone(res.phone_number)}
                        title="Copiar n\u00famero"
                        style={{ width: 24, height: 24 }}
                      >
                        <HiOutlineDocumentDuplicate size={14} />
                      </button>
                    </div>
                  </td>
                  <td className="hidden md:table-cell">{formatReservationDate(res.reservation_date)}</td>
                  <td>{slotToTime(res.init)}</td>
                  <td>{res.n_people}</td>
                  <td className="hidden lg:table-cell">{res.tag || res.mesa_num}</td>
                  <td>
                    <span className={cfg.badge}>{cfg.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mp-table-footer">
        {currentTableData.length} reservaci{currentTableData.length === 1 ? '\u00f3n' : 'ones'}
      </div>
    </div>
  );
};

export default Table;
